/* frontend/src/hooks/useVote.js
   -------------------------------------------------
   Real toggle, backed by the user's own row in skill_requests — not
   localStorage. The old version scoped "voted" to the browser (so the
   same account looked unvoted on a different device) and always
   inserted with a null user_id (so nothing tied a vote to an account).
   Both are fixed here: the source of truth for "did I vote" is now
   `skill_requests` filtered to auth.uid() = user_id (which is also all
   RLS lets a client read), and every write carries the real user_id.

   Tap once: insert (a stale tab or double-tap racing two inserts is
   treated as a benign no-op — see the 23505 handling below). Tap again:
   delete. People mis-tap and change their minds, so unvoting is a hard
   requirement, not an edge case.
*/
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@services/api.js';
import { useAuth } from '@contexts/AuthContext';

export const VOTE_EVENT = 'ca_vote_update';

// Legacy one-way-vote flags from the old localStorage flow. They no
// longer mean anything (the database row is the only source of truth
// now) and keeping them around risks a stale 'true' shadowing the real
// per-device-independent state during development/testing — cleared
// once, cheaply, rather than left to confuse a future reader.
const LEGACY_KEY_PREFIX = 'ca_voted_';
const clearLegacyVoteKeys = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(LEGACY_KEY_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // best-effort cleanup only
  }
};

export const useVote = (skillId, source = 'unknown') => {
  const { user } = useAuth();
  const [voted, setVoted] = useState(false);
  const [loading, setLoading] = useState(true); // true until the initial own-row check resolves
  const [error, setError] = useState(null);

  useEffect(() => { clearLegacyVoteKeys(); }, []);

  // Source of truth on mount: does my own row exist for this candidate?
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) { setVoted(false); setLoading(false); return; }
    setLoading(true);
    (async () => {
      const { data, error: fetchErr } = await supabase
        .from('skill_requests')
        .select('id')
        .eq('user_id', user.id)
        .eq('skill_name', skillId)
        .maybeSingle();
      if (cancelled) return;
      if (fetchErr) { setError(fetchErr.message); setLoading(false); return; }
      setVoted(!!data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id, skillId]);

  // Same-tab sync between every useVote instance for this skillId (e.g.
  // a leaderboard chip and any other surface rendering the same id).
  useEffect(() => {
    const handler = (e) => { if (e.detail?.id === skillId) setVoted(e.detail.voted); };
    window.addEventListener(VOTE_EVENT, handler);
    return () => window.removeEventListener(VOTE_EVENT, handler);
  }, [skillId]);

  const vote = useCallback(async () => {
    if (loading || !user?.id) return;
    const wasVoted = voted;
    setError(null);
    setVoted(!wasVoted); // optimistic
    setLoading(true);
    try {
      if (wasVoted) {
        const { error: delErr } = await supabase
          .from('skill_requests')
          .delete()
          .eq('user_id', user.id)
          .eq('skill_name', skillId);
        if (delErr) throw delErr;
      } else {
        const { error: insErr } = await supabase
          .from('skill_requests')
          .insert({ user_id: user.id, skill_name: skillId, email: user.email, reason: source });
        // A stale tab or double-tap can race two inserts for the same
        // (user_id, skill_name). The obvious guard — upsert with
        // onConflict: 'user_id,skill_name' — turned out not to work here:
        // skill_requests_user_skill_uniq is a PARTIAL unique index (WHERE
        // user_id IS NOT NULL), and PostgREST's upsert can only target a
        // full constraint, so it 400s with "no unique or exclusion
        // constraint matching the ON CONFLICT specification". A plain
        // insert that treats 23505 (unique_violation) as a benign no-op
        // gets the identical double-tap safety without that limitation.
        if (insErr && insErr.code !== '23505') throw insErr;
      }
      window.dispatchEvent(new CustomEvent(VOTE_EVENT, { detail: { id: skillId, voted: !wasVoted } }));
    } catch (err) {
      // A vote that did not land must not look like it did.
      setVoted(wasVoted);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [voted, loading, skillId, source, user]);

  return { voted, loading, error, vote };
};

export default useVote;
